import { ArrowRightOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Divider, Flex, Input, Typography } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../hooks/redux-hooks";
import { useFuseSearch } from "../../../hooks/useFuseSearch";
import { setAITemplateModalState, setCustomTemplateSelectedTab, setSelectedView } from "../../../slices/appStateSlice";
import styles from "./AITemplateModal.module.scss";
import List from "./List";
import TemplateListContainer from "./TemplateListContainer.component";
import { type CustomNote, TabType, ViewType } from "./types";

// Module-level so the Fuse index isn't rebuilt on every render.
const SEARCH_KEYS = ["name"];

const ListContainer = ({
  handleEditTemplate,
  customNoteList,
  fetchCustomNoteList,
  isListLoading,
}: {
  setIsPrimaryButtonDisabled?: (disabled: boolean) => void;
  handleEditTemplate: (template: CustomNote) => void;
  isListLoading: boolean;
  customNoteList: CustomNote[];
  fetchCustomNoteList: () => void;
}) => {
  const componentName = "ai-template-modal";
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const navigateToTemplateGallery = () => {
    dispatch(setAITemplateModalState(false));
    dispatch(setSelectedView(ViewType.TEMPLATE));
    navigate("/templates");
  };

  function Header() {
    return (
      <>
        <div className={styles[`${componentName}__header`]}>
          <div className={styles[`${componentName}__header-container`]}>
            <button
              onClick={() => dispatch(setAITemplateModalState(false))}
              className={styles[`${componentName}__header-close-button`]}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                width={20}
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
              </svg>
            </button>

            <h3>My Templates</h3>
          </div>
          <Button
            type="primary"
            onClick={() => dispatch(setCustomTemplateSelectedTab(TabType.CREATE))}
            icon={<PlusOutlined />}
            aria-label="Create Template"
            title="Create Template"
          >
            Create Template
          </Button>
        </div>
        <Divider style={{ marginBlock: "0" }} />
      </>
    );
  }

  const userOwnedCustomNotes = customNoteList.filter((note) => note.is_owner);
  const sharedCustomNotes = customNoteList.filter((note) => !note.is_owner);

  // One Fuse over the whole list (own + group-shared) so a query matches templates in either
  // section; the sections are re-derived from the match set below to keep their original order.
  const searchableNotes = useMemo(
    () => customNoteList.map((note) => ({ pk: note.pk, name: note.name })),
    [customNoteList],
  );
  const { search: fuseSearch } = useFuseSearch({ data: searchableNotes, keys: SEARCH_KEYS });

  const isSearching = debouncedQuery.length > 0;
  const matchedPks = isSearching ? new Set(fuseSearch(debouncedQuery).map((note) => note.pk)) : null;

  const visibleOwnedNotes = matchedPks
    ? userOwnedCustomNotes.filter((note) => matchedPks.has(note.pk))
    : userOwnedCustomNotes;
  const visibleSharedNotes = matchedPks
    ? sharedCustomNotes.filter((note) => matchedPks.has(note.pk))
    : sharedCustomNotes;
  const hasCustomNoteMatches = visibleOwnedNotes.length > 0 || visibleSharedNotes.length > 0;

  return (
    <>
      <Header />
      <div className={styles[`${componentName}__search`]}>
        <Input
          placeholder="Search templates..."
          prefix={<SearchOutlined style={{ color: "#666" }} />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          allowClear
        />
      </div>
      {/* While searching, a section with no matches is dropped entirely — showing "My Templates"
          with the create-your-first empty state under a query reads as "you have none". */}
      {(!isSearching || visibleOwnedNotes.length > 0) && (
        <>
          <div className={styles[`${componentName}__list-header`]}>My Templates</div>
          <List
            handleEditTemplate={handleEditTemplate}
            customNoteList={visibleOwnedNotes}
            fetchCustomNoteList={fetchCustomNoteList}
            isListLoading={isListLoading}
            isCompact={isSearching}
          />
        </>
      )}
      {visibleSharedNotes.length > 0 && (
        <>
          <div className={styles[`${componentName}__list-header`]} style={{ marginTop: "20px" }}>
            Shared with me
          </div>
          <List
            handleEditTemplate={handleEditTemplate}
            customNoteList={visibleSharedNotes}
            fetchCustomNoteList={fetchCustomNoteList}
            isListLoading={isListLoading}
            isCompact={isSearching}
            isSharedList={true}
          />
        </>
      )}
      {isSearching && !hasCustomNoteMatches && !isListLoading && (
        <div className={styles[`${componentName}__search-empty`]}>
          <Typography.Text type="secondary">No templates of yours match "{debouncedQuery}"</Typography.Text>
        </div>
      )}
      <Flex className={styles[`${componentName}__list-header-cont`]} style={{ marginTop: "20px" }}>
        <div className={styles[`${componentName}__list-header`]}>TEMPLATE GALLERY</div>
        <Button onClick={navigateToTemplateGallery} className={styles[`${componentName}__list-header-button`]}>
          200+ templates | View all templates <ArrowRightOutlined />
        </Button>
      </Flex>
      <TemplateListContainer searchQuery={debouncedQuery} />
    </>
  );
};

export default ListContainer;
